import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import theme, { tokens } from './theme';
import { AUTH_REDIRECT_EVENT, buildLoginUrl, getPostAuthDestination, sanitizeReturnTo } from './utils/authRedirect';

import WelcomePage from './pages/WelcomePage';
import LoginPage from './pages/LoginPageEnhanced';
import SignUpPage from './pages/SignUpPageEnhanced';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import AdminDashboard from './pages/AdminDashboardEnhanced';
import UserDashboard from './pages/UserDashboardEnhanced';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import BillingPage from './pages/BillingPage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingGate from './components/OnboardingGate';

function FullScreenStatus({ title, subtitle }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: tokens.bg,
        color: tokens.text,
        fontFamily: '"Oxygen", sans-serif',
        padding: '24px',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: '440px' }}>
        <div style={{ fontSize: '1.35rem', fontWeight: 600 }}>{title}</div>
        {subtitle ? (
          <div style={{ marginTop: '0.75rem', color: tokens.textMuted, lineHeight: 1.6 }}>
            {subtitle}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AuthRedirectHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthRedirect = (event) => {
      const returnTo = sanitizeReturnTo(event.detail?.returnTo);
      const reason = event.detail?.reason || 'session-expired';

      navigate(buildLoginUrl({ returnTo, reason }), {
        replace: true,
        state: { returnTo, reason },
      });
    };

    window.addEventListener(AUTH_REDIRECT_EVENT, handleAuthRedirect);
    return () => window.removeEventListener(AUTH_REDIRECT_EVENT, handleAuthRedirect);
  }, [navigate]);

  return null;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const loginReturnTo = sanitizeReturnTo(new URLSearchParams(location.search).get('returnTo'));
  const postAuthDestination = getPostAuthDestination(user, loginReturnTo);

  if (loading) {
    return <FullScreenStatus title="Loading FastTime..." subtitle="Restoring your session and getting your dashboard ready." />;
  }

  return (
    <Routes>
      <Route path="/" element={<WelcomePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/login" element={user ? <Navigate to={postAuthDestination} replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to={postAuthDestination} replace /> : <ForgotPasswordPage />} />
      <Route path="/signup" element={user ? <Navigate to={postAuthDestination} replace /> : <SignUpPage />} />
      <Route
        path="/onboarding"
        element={
          <PrivateRoute>
            <OnboardingPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <OnboardingGate>
              {user?.isAdmin ? (
                <Navigate to="/admin/overview" replace />
              ) : (
                // Preserve the query string (in particular ?preview=1) -- a
                // plain string `to` drops it, so a preview visitor landing on
                // bare /dashboard lost preview mode before the dashboard
                // itself ever mounted.
                <Navigate to={{ pathname: '/dashboard/daily-state', search: location.search }} replace />
              )}
            </OnboardingGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboard/:section"
        element={
          <PrivateRoute>
            <OnboardingGate>
              {user?.isAdmin ? <Navigate to="/admin/overview" replace /> : <UserDashboard />}
            </OnboardingGate>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute requireAdmin>
            <Navigate to="/admin/overview" replace />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/:section"
        element={
          <PrivateRoute requireAdmin>
            <AdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/delete-account"
        element={
          <PrivateRoute>
            <DeleteAccountPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <PrivateRoute>
            <BillingPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            {/* Same fix as the /dashboard redirect above: preserve the query
                string so ?preview=1 survives the hop instead of being
                dropped by a plain string `to`. */}
            <Navigate
              to={{
                pathname: user?.isAdmin ? '/admin/settings' : '/dashboard/settings',
                search: location.search,
              }}
              replace
            />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthRedirectHandler />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}
